import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users,
  Lock,
  Loader2,
  CheckCircle2,
  Activity,
  Search,
  Download,
  X,
  Copy,
  Check,
  TrendingUp,
  ShieldCheck,
  Cpu,
  Radio,
  KeyRound,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface OracleSessionsLocksViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface SessionsSummary {
  sessions: number;
  sessions_max: number;
  sessions_active: number;
  sessions_util_pct: number;
  processes: number;
  processes_max: number;
  processes_util_pct: number;
  blocked_sessions: number;
  open_transactions: number;
  oldest_transaction_sec: number | null;
}

interface UserSessionItem {
  username: string;
  sessions: number;
  hostname?: string;
  ip?: string;
  os?: string;
  config_id?: string;
  collected_at?: string;
  db_type?: string;
}

interface LocksSummary {
  blocked_sessions: number;
  locked_objects: number;
  deadlocks_per_sec: number;
}

interface BlockingSessionItem {
  session_id?: string | number;
  blocking_session_id?: string | number;
  username?: string;
  program?: string;
  sql_id?: string;
  wait_event?: string;
  duration_sec?: number;
  [key: string]: any;
}

interface LockedObjectItem {
  object_name?: string;
  owner?: string;
  object_type?: string;
  lock_mode?: string;
  session_id?: string | number;
  [key: string]: any;
}

interface ConnectionGraphSummary {
  latest?: {
    connections_active: number;
    connections_idle: number;
    connections_waiting: number;
    connections_total: number;
    connections_util_pct: number;
  };
  peak?: any;
  average?: any;
  total?: number;
}

export const OracleSessionsLocksView: React.FC<OracleSessionsLocksViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState<'overview' | 'sessions' | 'locks'>('overview');

  // Sessions API data
  const [sessionsData, setSessionsData] = useState<{
    summary: SessionsSummary | null;
    sessions_by_user: UserSessionItem[];
    open_transactions: any[];
    collected_at?: string;
  }>({
    summary: null,
    sessions_by_user: [],
    open_transactions: [],
  });

  // Connections Graph API data
  const [graphData, setGraphData] = useState<{
    labels: string[];
    series: {
      connections_active?: (number | null)[];
      connections_idle?: (number | null)[];
      connections_waiting?: (number | null)[];
      connections_total?: (number | null)[];
      connections_util_pct?: (number | null)[];
    };
    summary: ConnectionGraphSummary | null;
  }>({
    labels: [],
    series: {},
    summary: null,
  });

  // Locks API data
  const [locksData, setLocksData] = useState<{
    summary: LocksSummary | null;
    blocking_sessions: BlockingSessionItem[];
    locked_objects: LockedObjectItem[];
    collected_at?: string;
  }>({
    summary: null,
    blocking_sessions: [],
    locked_objects: [],
  });

  const [userSearch, setUserSearch] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    if (!activeCluster) return;
    try {
      const res = await baseapi.get('/db_monitor_dashboard/oracle/sessions', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      if (res.data?.data) {
        setSessionsData({
          summary: res.data.data.summary || null,
          sessions_by_user: res.data.data.sessions_by_user || [],
          open_transactions: res.data.data.open_transactions || [],
          collected_at: res.data.collected_at,
        });
      }
    } catch (err) {
      console.warn('Oracle sessions fetch error:', err);
    }
  }, [activeCluster, clientId]);

  const fetchConnectionsGraph = useCallback(async () => {
    if (!activeCluster) return;
    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/connections', {
        params: {
          cluster_id: activeCluster,
          client_id: clientId,
          time_range: timeRange,
        },
      });
      if (res.data?.data) {
        setGraphData({
          labels: res.data.data.labels || [],
          series: res.data.data.series || {},
          summary: res.data.data.summary || null,
        });
      }
    } catch (err) {
      console.warn('Oracle connections graph fetch error:', err);
    }
  }, [activeCluster, clientId, timeRange]);

  const fetchLocks = useCallback(async () => {
    if (!activeCluster) return;
    try {
      const res = await baseapi.get('/db_monitor_dashboard/oracle/locks', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      if (res.data?.data) {
        setLocksData({
          summary: res.data.data.summary || null,
          blocking_sessions: res.data.data.blocking_sessions || [],
          locked_objects: res.data.data.locked_objects || [],
          collected_at: res.data.collected_at,
        });
      }
    } catch (err) {
      console.warn('Oracle locks fetch error:', err);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSessions(), fetchConnectionsGraph(), fetchLocks()]).finally(() => {
      setLoading(false);
    });
  }, [fetchSessions, fetchConnectionsGraph, fetchLocks, refreshKey]);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  // Filtered users
  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return sessionsData.sessions_by_user;
    const q = userSearch.toLowerCase();
    return sessionsData.sessions_by_user.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        (u.hostname && u.hostname.toLowerCase().includes(q)) ||
        (u.ip && u.ip.includes(q))
    );
  }, [sessionsData.sessions_by_user, userSearch]);

  // Chart data formatting
  const formattedGraphData = useMemo(() => {
    if (!graphData.labels || graphData.labels.length === 0) return [];
    const active = graphData.series.connections_active || [];
    const idle = graphData.series.connections_idle || [];
    const waiting = graphData.series.connections_waiting || [];
    const total = graphData.series.connections_total || [];
    const util = graphData.series.connections_util_pct || [];

    return graphData.labels.map((lbl, idx) => {
      const d = new Date(lbl);
      const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return {
        time: timeStr,
        fullTime: d.toLocaleString(),
        active: active[idx] ?? 0,
        idle: idle[idx] ?? 0,
        waiting: waiting[idx] ?? 0,
        total: total[idx] ?? 0,
        util: util[idx] ?? 0,
      };
    });
  }, [graphData]);

  const exportSessionsCSV = () => {
    const headers = ['Username', 'Sessions Count', 'Hostname', 'IP Address', 'OS', 'DB Type'];
    const rows = sessionsData.sessions_by_user.map((u) => [
      `"${u.username}"`,
      u.sessions,
      `"${u.hostname || ''}"`,
      `"${u.ip || ''}"`,
      `"${u.os || ''}"`,
      `"${u.db_type || 'oracle'}"`,
    ]);
    const csvContent =
      'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', encodeURI(csvContent));
    downloadAnchor.setAttribute('download', `oracle_sessions_${activeCluster}.csv`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  if (loading) {
    return (
      <div className="bg-white/60 dark:bg-gray-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl min-h-[420px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Loading Oracle sessions and locks data...
          </span>
        </div>
      </div>
    );
  }

  const sSummary = sessionsData.summary;
  const lSummary = locksData.summary;

  return (
    <div className="space-y-4">
      {/* Top Banner KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {/* Total Sessions */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Total Sessions
            </div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">
              {sSummary?.sessions ?? 0}{' '}
              <span className="text-xs text-gray-400 font-normal">/ {sSummary?.sessions_max ?? 322}</span>
            </div>
          </div>
        </div>

        {/* Active Sessions */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
            <Radio className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Active Sessions
            </div>
            <div className="text-base font-bold text-emerald-600 dark:text-emerald-400 font-mono">
              {sSummary?.sessions_active ?? 0}
            </div>
          </div>
        </div>

        {/* Oracle Processes */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
            <Cpu className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Processes
            </div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">
              {sSummary?.processes ?? 0}{' '}
              <span className="text-xs text-gray-400 font-normal">/ {sSummary?.processes_max ?? 200}</span>
            </div>
          </div>
        </div>

        {/* Blocked Sessions */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div
            className={`p-2 rounded-lg border ${(sSummary?.blocked_sessions ?? 0) > 0 || (lSummary?.blocked_sessions ?? 0) > 0
                ? 'bg-red-500/10 text-red-600 border-red-500/30'
                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
              }`}
          >
            <Lock className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Blocked Sessions
            </div>
            <div
              className={`text-base font-bold font-mono ${(sSummary?.blocked_sessions ?? 0) > 0 || (lSummary?.blocked_sessions ?? 0) > 0
                  ? 'text-red-600'
                  : 'text-emerald-600 dark:text-emerald-400'
                }`}
            >
              {sSummary?.blocked_sessions ?? lSummary?.blocked_sessions ?? 0}
            </div>
          </div>
        </div>

        {/* Locked Objects */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div
            className={`p-2 rounded-lg border ${(lSummary?.locked_objects ?? 0) > 0
                ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
                : 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20'
              }`}
          >
            <KeyRound className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Locked Objects
            </div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">
              {lSummary?.locked_objects ?? 0}
            </div>
          </div>
        </div>

        {/* Open Transactions */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
            <Activity className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Open Txns
            </div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">
              {sSummary?.open_transactions ?? 0}
            </div>
          </div>
        </div>
      </div>

      {/* Sub-navigation Switcher */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-800 pb-2">
        <button
          onClick={() => setActiveSubTab('overview')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${activeSubTab === 'overview'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          <span>Activity & Connections Trend</span>
        </button>

        <button
          onClick={() => setActiveSubTab('sessions')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${activeSubTab === 'sessions'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span>Sessions by User</span>
          <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono bg-white/20">
            {sessionsData.sessions_by_user.length}
          </span>
        </button>

        <button
          onClick={() => setActiveSubTab('locks')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${activeSubTab === 'locks'
              ? 'bg-orange-500 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
        >
          <Lock className="w-3.5 h-3.5" />
          <span>Locks & Blocked Sessions</span>
          {(locksData.blocking_sessions.length > 0 || locksData.locked_objects.length > 0) && (
            <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono bg-red-500 text-white animate-pulse">
              {locksData.blocking_sessions.length + locksData.locked_objects.length}
            </span>
          )}
        </button>
      </div>

      {/* View Content based on sub-tab */}
      {(activeSubTab === 'overview' || activeSubTab === 'sessions') && (
        /* Section: Connections Trend Chart */
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-800 pb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-emerald-500 text-white">
                <TrendingUp className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                  Connections & Sessions Over Time
                </h3>
                <p className="text-[10px] text-gray-500">
                  Real-time trend of active, idle, and waiting client connections
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                Latest Active: {graphData.summary?.latest?.connections_active ?? 1}
              </span>
              <span className="text-[10px] font-mono font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">
                Total: {graphData.summary?.latest?.connections_total ?? 2}
              </span>
            </div>
          </div>

          <div className="h-64 w-full">
            {formattedGraphData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedGraphData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="activeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="idleGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 9, fill: '#888888' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 9, fill: '#888888' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#111827',
                      borderColor: '#374151',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                    labelStyle={{ color: '#F97316', fontWeight: 'bold' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="active"
                    stroke="#10B981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#activeGrad)"
                    name="Active Connections"
                  />
                  <Area
                    type="monotone"
                    dataKey="idle"
                    stroke="#3B82F6"
                    strokeWidth={1.5}
                    fillOpacity={1}
                    fill="url(#idleGrad)"
                    name="Idle Connections"
                  />
                  <Line
                    type="monotone"
                    dataKey="waiting"
                    stroke="#EF4444"
                    strokeWidth={2}
                    dot={false}
                    name="Waiting Connections"
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '10px', top: -5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-gray-400 italic">
                No connection history data available.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section: Sessions by User Table */}
      {(activeSubTab === 'overview' || activeSubTab === 'sessions') && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-2.5">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
                <Users className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                  Oracle Sessions by Database User
                </h3>
                <span className="text-[10px] text-gray-500">
                  Breakdown of active client sessions grouped by Oracle schema / account
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-48">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search username or host..."
                  className="w-full bg-gray-50 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-700/80 rounded-lg pl-8 pr-3 py-1 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-orange-500 h-7"
                />
                {userSearch && (
                  <button
                    onClick={() => setUserSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              <button
                onClick={exportSessionsCSV}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 text-xs font-semibold h-7 cursor-pointer"
                title="Export session list as CSV"
              >
                <Download className="w-3.5 h-3.5 text-orange-500" />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-800 rounded-lg">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                  <th className="py-2.5 px-3">Oracle User / Schema</th>
                  <th className="py-2.5 px-3 text-center">Session Count</th>
                  <th className="py-2.5 px-3">Client Host</th>
                  <th className="py-2.5 px-3">IP Address</th>
                  <th className="py-2.5 px-3">OS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800/40 text-gray-700 dark:text-gray-200">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((u, idx) => {
                    const isSystemUser = ['SYS', 'SYSTEM', 'DBSNMP', 'OUTLN'].includes(u.username);
                    return (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors border-b border-gray-100 dark:border-gray-800/30 h-8"
                      >
                        {/* Username */}
                        <td className="py-1 px-3 whitespace-nowrap font-mono">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-orange-600 dark:text-orange-400 text-xs">
                              {u.username}
                            </span>
                            {isSystemUser && (
                              <span className="px-1.5 py-0.2 rounded text-[9px] font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                                SYSTEM
                              </span>
                            )}
                            <button
                              onClick={() => handleCopy(u.username, `user-${u.username}`)}
                              className="text-gray-400 hover:text-orange-500 p-0.5 cursor-pointer"
                              title="Copy username"
                            >
                              {copiedKey === `user-${u.username}` ? (
                                <Check className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </td>

                        {/* Sessions Count */}
                        <td className="py-1 px-3 text-center whitespace-nowrap font-mono font-bold">
                          <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            {u.sessions}
                          </span>
                        </td>

                        {/* Hostname */}
                        <td className="py-1 px-3 whitespace-nowrap font-mono text-gray-600 dark:text-gray-400">
                          {u.hostname || '—'}
                        </td>

                        {/* IP */}
                        <td className="py-1 px-3 whitespace-nowrap font-mono text-gray-600 dark:text-gray-400">
                          {u.ip || '—'}
                        </td>

                        {/* OS */}
                        <td className="py-1 px-3 whitespace-nowrap font-mono uppercase text-gray-500">
                          {u.os || 'linux'}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500 italic">
                      No active sessions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section: Locks, Blocked Sessions & Locked Objects */}
      {(activeSubTab === 'overview' || activeSubTab === 'locks') && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Blocking & Blocked Sessions */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-red-500 text-white">
                  <Lock className="w-3.5 h-3.5" />
                </div>
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Blocked / Blocking Sessions
                </h4>
              </div>

              <span
                className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border ${locksData.blocking_sessions.length > 0
                    ? 'bg-red-500/10 text-red-600 border-red-500/20'
                    : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                  }`}
              >
                {locksData.blocking_sessions.length} Blocked
              </span>
            </div>

            {locksData.blocking_sessions.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto custom-scroll">
                {locksData.blocking_sessions.map((b, idx) => (
                  <div
                    key={idx}
                    className="p-2.5 rounded-lg bg-red-500/5 border border-red-500/20 text-xs font-mono space-y-1"
                  >
                    <div className="flex justify-between font-bold text-red-600 dark:text-red-400">
                      <span>Session #{b.session_id}</span>
                      <span>Blocked by #{b.blocking_session_id}</span>
                    </div>
                    {b.username && <div className="text-gray-500 text-[11px]">User: {b.username}</div>}
                    {b.wait_event && (
                      <div className="text-amber-500 text-[11px]">Event: {b.wait_event}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center rounded-lg bg-emerald-50/50 dark:bg-emerald-950/10 border border-emerald-500/20 space-y-2">
                <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto" />
                <div className="text-xs font-bold text-emerald-700 dark:text-emerald-400">
                  No Blocked Sessions
                </div>
                <p className="text-[11px] text-gray-500 max-w-sm mx-auto">
                  Oracle database is running smoothly with zero lock contention or session blocks detected.
                </p>
              </div>
            )}
          </div>

          {/* Locked Objects */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-purple-500 text-white">
                  <KeyRound className="w-3.5 h-3.5" />
                </div>
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Locked Objects & Tables
                </h4>
              </div>

              <span className="text-[10px] font-mono font-bold text-purple-600 dark:text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                {locksData.locked_objects.length} Locked
              </span>
            </div>

            {locksData.locked_objects.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto custom-scroll">
                {locksData.locked_objects.map((obj, idx) => (
                  <div
                    key={idx}
                    className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-xs font-mono space-y-1"
                  >
                    <div className="flex justify-between font-bold text-gray-900 dark:text-white">
                      <span>{obj.object_name}</span>
                      <span className="text-orange-500">{obj.lock_mode || 'EXCLUSIVE'}</span>
                    </div>
                    <div className="flex gap-4 text-[11px] text-gray-500">
                      <span>Owner: {obj.owner}</span>
                      <span>Type: {obj.object_type}</span>
                      <span>Held by Session: #{obj.session_id}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center rounded-lg bg-gray-50 dark:bg-gray-950/50 border border-gray-200 dark:border-gray-800 space-y-2">
                <CheckCircle2 className="w-8 h-8 text-gray-400 mx-auto" />
                <div className="text-xs font-bold text-gray-700 dark:text-gray-300">
                  No Locked Objects
                </div>
                <p className="text-[11px] text-gray-500 max-w-sm mx-auto">
                  No DML or DDL table locks currently held in the Oracle data dictionary.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OracleSessionsLocksView;
